import { BLE_CONFIG } from '@/config/ble'
import { decodePacket } from '../decoder/packetDecoder'
import { validateSensorPacket } from '../validator/packetValidator'
import { useBleStore } from '@/state/bleStore'
import { useSensorStore } from '@/state/sensorStore'
import type { SensorPacket } from '../types'
import type { SensorPlacement } from '../types'

type PacketListener = (packet: SensorPacket) => void

interface DeviceConnection {
  device: BluetoothDevice
  characteristic: BluetoothRemoteGATTCharacteristic | null
  intentionalDisconnect: boolean
  reconnectAttempts: number
  reconnectTimeoutId: ReturnType<typeof setTimeout> | null
}

const SENSOR_PLACEMENTS: SensorPlacement[] = ['leftArm', 'rightArm', 'leftHand', 'rightHand']

function isPlacementUsedByOtherDevice(
  devices: Record<string, { placement: SensorPlacement }>,
  placement: SensorPlacement,
  deviceId?: string,
): boolean {
  return Object.entries(devices).some(
    ([id, device]) => id !== deviceId && device.placement === placement,
  )
}

function getFirstAvailablePlacement(
  devices: Record<string, { placement: SensorPlacement }>,
  preferred?: SensorPlacement,
  deviceId?: string,
): SensorPlacement {
  if (preferred && !isPlacementUsedByOtherDevice(devices, preferred, deviceId)) {
    return preferred
  }

  return (
    SENSOR_PLACEMENTS.find(
      (placement) => !isPlacementUsedByOtherDevice(devices, placement, deviceId),
    ) ?? 'leftArm'
  )
}

class BleService {
  private readonly connections = new Map<string, DeviceConnection>()
  private packetListeners: PacketListener[] = []

  // ─── Public API ────────────────────────────────────────────────────────────

  async connect(): Promise<void> {
    const { deviceOrder, devices, setStatus, setError, upsertDevice, setDevice } =
      useBleStore.getState()
    let selectedDeviceId: string | null = null
    let provisionalConnectionAdded = false

    // Use store-tracked active devices to decide UX limits.
    // Internal connection map may transiently contain stale entries after failed attempts.
    if (deviceOrder.length >= 2) {
      setError('Maximum of 2 BLE devices supported in this mode')
      return
    }

    try {
      setStatus('scanning')

      const device = await navigator.bluetooth.requestDevice({
        filters: [{ services: [BLE_CONFIG.SERVICE_UUID] }],
      })
      selectedDeviceId = device.id

      if (this.connections.has(device.id)) {
        setStatus('connected')
        return
      }

      const previousPlacement = devices[device.id]?.placement
      const placement = getFirstAvailablePlacement(devices, previousPlacement, device.id)

      const connection: DeviceConnection = {
        device,
        characteristic: null,
        intentionalDisconnect: false,
        reconnectAttempts: 0,
        reconnectTimeoutId: null,
      }

      this.connections.set(device.id, connection)
      provisionalConnectionAdded = true

      setStatus('connecting')
      device.addEventListener('gattserverdisconnected', () => this.onGattDisconnected(device.id))

      await this.subscribeToCharacteristic(device.id)

      upsertDevice({
        id: device.id,
        name: device.name ?? 'Unknown',
        status: 'connected',
        error: null,
        reconnectAttempt: 0,
        placement,
        hz: 0,
      })

      setDevice({ id: device.id, name: device.name ?? 'Unknown' })
      setStatus('connected')
    } catch (err) {
      if (selectedDeviceId && provisionalConnectionAdded) {
        this.cancelPendingReconnect(selectedDeviceId)
        this.connections.delete(selectedDeviceId)
      }
      const message = err instanceof Error ? err.message : 'Connection failed'
      setError(message)
    }
  }

  async disconnect(deviceId?: string): Promise<void> {
    const targetId = deviceId ?? useBleStore.getState().device?.id
    if (!targetId) return

    const connection = this.connections.get(targetId)
    if (!connection) return

    connection.intentionalDisconnect = true
    this.cancelPendingReconnect(targetId)
    await this.unsubscribeCharacteristic(targetId)

    if (connection.device.gatt?.connected) {
      connection.device.gatt.disconnect()
    }

    this.connections.delete(targetId)
    useSensorStore.getState().removeDevice(targetId)
    useBleStore.getState().removeDevice(targetId)

    const remaining = useBleStore.getState().deviceOrder
    if (remaining.length === 0) {
      useBleStore.getState().reset()
      return
    }

    const first = useBleStore.getState().devices[remaining[0]]
    if (first) {
      useBleStore.getState().setDevice({ id: first.id, name: first.name })
      useBleStore.getState().setStatus('connected')
    }
  }

  async disconnectAll(): Promise<void> {
    const deviceIds = [...useBleStore.getState().deviceOrder]

    for (const deviceId of deviceIds) {
      const connection = this.connections.get(deviceId)
      if (connection) {
        connection.intentionalDisconnect = true
        this.cancelPendingReconnect(deviceId)
        await this.unsubscribeCharacteristic(deviceId)

        if (connection.device.gatt?.connected) {
          connection.device.gatt.disconnect()
        }

        this.connections.delete(deviceId)
      }

      useSensorStore.getState().removeDevice(deviceId)
      useBleStore.getState().removeDevice(deviceId)
    }

    useBleStore.getState().reset()
  }

  setPlacement(deviceId: string, placement: SensorPlacement): void {
    const { devices } = useBleStore.getState()
    if (isPlacementUsedByOtherDevice(devices, placement, deviceId)) {
      return
    }
    useBleStore.getState().setDevicePlacement(deviceId, placement)
  }

  onPacket(listener: PacketListener): () => void {
    this.packetListeners.push(listener)
    return () => {
      this.packetListeners = this.packetListeners.filter((l) => l !== listener)
    }
  }

  // ─── Internal ──────────────────────────────────────────────────────────────

  private async subscribeToCharacteristic(deviceId: string): Promise<void> {
    const connection = this.connections.get(deviceId)
    if (!connection?.device.gatt) throw new Error('No GATT server')

    const server = await connection.device.gatt.connect()
    const service = await server.getPrimaryService(BLE_CONFIG.SERVICE_UUID)
    connection.characteristic = await service.getCharacteristic(BLE_CONFIG.CHARACTERISTIC_UUID)

    await connection.characteristic.startNotifications()
    connection.characteristic.addEventListener('characteristicvaluechanged', (event) =>
      this.onNotification(deviceId, event),
    )
  }

  private async unsubscribeCharacteristic(deviceId: string): Promise<void> {
    const connection = this.connections.get(deviceId)
    if (!connection?.characteristic) return

    try {
      await connection.characteristic.stopNotifications()
    } catch {
      // best-effort — device may already be gone
    }
    connection.characteristic = null
  }

  private onNotification = (deviceId: string, event: Event): void => {
    const raw = (event.target as BluetoothRemoteGATTCharacteristic).value
    if (!raw) return

    const decoded = decodePacket(BLE_CONFIG.DECODER_PROFILE, raw)
    if (!decoded) return

    const packet = validateSensorPacket(decoded)
    if (!packet) return

    useSensorStore.getState().setLatestPacket(deviceId, packet)
    const hz = useSensorStore.getState().byDevice[deviceId]?.hz ?? 0
    useBleStore.getState().setDeviceHz(deviceId, hz)
    this.packetListeners.forEach((l) => l(packet))
  }

  private onGattDisconnected = (deviceId: string): void => {
    const connection = this.connections.get(deviceId)
    if (!connection || connection.intentionalDisconnect) return

    connection.characteristic = null
    useBleStore.getState().setDeviceStatus(deviceId, 'reconnecting')
    useBleStore.getState().setStatus('reconnecting')
    this.scheduleReconnect(deviceId)
  }

  private scheduleReconnect(deviceId: string): void {
    const connection = this.connections.get(deviceId)
    if (!connection) return

    if (connection.reconnectAttempts >= BLE_CONFIG.RECONNECT_MAX_ATTEMPTS) {
      useBleStore
        .getState()
        .setDeviceError(
          deviceId,
          `Lost connection after ${BLE_CONFIG.RECONNECT_MAX_ATTEMPTS} reconnect attempts`,
        )
      connection.reconnectAttempts = 0
      return
    }

    const delay = BLE_CONFIG.RECONNECT_BASE_DELAY_MS * Math.pow(2, connection.reconnectAttempts)
    connection.reconnectAttempts++
    useBleStore.getState().setDeviceReconnectAttempt(deviceId, connection.reconnectAttempts)

    connection.reconnectTimeoutId = setTimeout(async () => {
      // Timeout may fire after user disconnect/reset. Abort stale reconnect attempts.
      const latest = this.connections.get(deviceId)
      if (!latest || latest !== connection || latest.intentionalDisconnect) {
        return
      }

      try {
        await this.subscribeToCharacteristic(deviceId)

        const afterSubscribe = this.connections.get(deviceId)
        const stillTrackedInStore = Boolean(useBleStore.getState().devices[deviceId])
        if (
          !afterSubscribe ||
          afterSubscribe !== connection ||
          afterSubscribe.intentionalDisconnect ||
          !stillTrackedInStore
        ) {
          if (connection.device.gatt?.connected) {
            connection.device.gatt.disconnect()
          }
          this.connections.delete(deviceId)
          return
        }

        connection.reconnectAttempts = 0
        useBleStore.getState().setDeviceStatus(deviceId, 'connected')
        useBleStore.getState().setDeviceReconnectAttempt(deviceId, 0)
      } catch {
        const retryConnection = this.connections.get(deviceId)
        if (
          !retryConnection ||
          retryConnection !== connection ||
          retryConnection.intentionalDisconnect
        ) {
          return
        }
        this.scheduleReconnect(deviceId)
      }
    }, delay)
  }

  private cancelPendingReconnect(deviceId: string): void {
    const connection = this.connections.get(deviceId)
    if (!connection?.reconnectTimeoutId) return
    clearTimeout(connection.reconnectTimeoutId)
    connection.reconnectTimeoutId = null
  }
}

// Singleton — one BLE connection per session
export const bleService = new BleService()
